<script lang="ts">
  import { worldHour } from '../worldTime'
  /** D3 · NPC TREE — the graph it runs (the kit's Citizen graph: states, what each does, what moves it on), with the role it runs as.
   *  READOUT (G6a): what it is doing NOW — the npc-host runs every NPC with a home while no one watches; its state is lit in the tree,
   *  its drives and its last events beside a map of the place it lives in.
   *  THE TRACE (G6b): its loop as a circuit and its choices fanning out of it (the Chair's frames 7–9 — Keys' trace log), and the full
   *  tree of whichever decision is picked.
   *  TODAY (the day cycle, D3a — 10-23): its DAY as the capital shard runs it on the world clock — what it is doing now, in words, from when
   *  to when (the world's time: 15 game hours an hour), and the day's changes from its journal (readout · Day.now).
   *  ITS DAY AS A RING (NPC life, L1 — 10-23): the ring the host runs it on (its day's activities at its own things), the dot where the
   *  world clock is, a fan at each decision; the trace's circuit is that ring. */
  import { api } from '../api'
  import type { Readout, Rec } from '../types'
  import DecisionTree from './DecisionTree.svelte'
  import LoopDiagram from './LoopDiagram.svelte'
  import TraceView from './TraceView.svelte'
  import RingView from './RingView.svelte'
  let { rec }: { rec: Rec } = $props()
  let sel = $state<string | null>(null)
  /** where its OWN graph is: its state — or, while an object's behaviour runs, the state its graph paused in (the step that handed over) */
  const here = $derived.by(() => {
    if (!ro?.online) return null
    if (!ro.behaviour) return ro.state
    const l = ro.loop ?? []
    for (let i = l.length - 1; i >= 0; i--) { if (l[i].on === `behaviour:${ro.behaviour}`) return l[i].from; if (!l[i].inside) return l[i].to }
    return null
  })

  let ro = $state<Readout | null>(null)
  $effect(() => {
    const t = rec.tenant
    ro = null
    if (!t) return
    let alive = true
    const pull = () => api.readout(t).then((r) => { if (alive) ro = r }).catch(() => { /* the next beat */ })
    void pull()
    const iv = setInterval(pull, 2000)
    return () => { alive = false; clearInterval(iv) }
  })
  // the map: the place's things and where it stands (x right, z down — the plaza is 16 m across)
  const SPAN = 9
  const mx = (x: number) => ((x + SPAN) / (SPAN * 2)) * 100, mz = (z: number) => ((z + SPAN) / (SPAN * 2)) * 100
  /** the map's things: what it holds is with it (not on the map); a thing on another's spot labels a line lower */
  const things = $derived.by(() => {
    const out: { n: string; x: number; z: number; dy: number }[] = []
    for (const o of ro?.map ?? []) {
      if (o.n === ro?.held) continue
      const under = out.filter((p) => Math.hypot(p.x - o.x, p.z - o.z) < 0.6).length
      out.push({ n: o.n, x: o.x, z: o.z, dy: under * 3 })
    }
    return out
  })

  type State = { name: string; lod?: number; do: { task: string; [k: string]: unknown }[] }
  type Tr = { from: string; to: string; trigger: { kind: string; param?: number; topic?: string } }
  let graph = $state<{ initial: string; states: State[]; transitions: Tr[] } | null>(null)
  let error = $state<string | null>(null)
  $effect(() => {
    const url = rec.record.template.graph
    if (!url) return
    graph = null; error = null
    fetch(url).then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`)))).then((g) => (graph = g)).catch((e) => (error = `The graph could not be fetched (${e.message}).`))
  })
  /** the world clock's hh:mm at a real moment (worldTime.ts — the one clock), to the minute */
  const wclock = (iso: string) => { const m = Math.round(worldHour(Date.parse(iso)) * 60) % 1440; return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}` }
  /** today's changes (its journal's Day.now lines, newest first) */
  const dayLog = $derived((rec.journal ?? []).filter((j) => j.fn === 'Day.now').slice(0, 8).map((j) => ({ at: (j.after as { since?: string })?.since ?? j.at, says: (j.after as { says?: string })?.says ?? '' })))
  const trig = (t: Tr['trigger']) => `${t.kind}${t.param != null ? ` ${t.param}` : ''}${t.topic ? ` "${t.topic}"` : ''}`
  const task = (t: State['do'][number]) => [t.task, ...Object.entries(t).filter(([k]) => k !== 'task').map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)].join(' ')
</script>

<div class="wrap">
{#if ro?.day}
<section class="frame today">
  <h3 class="caps">Today <span class="muted">· its day on the world clock, as the capital shard runs it</span></h3>
  <p class="daynow"><span class={ro.day.live ? 'on caps' : 'off caps'}>● {ro.day.live ? 'Live' : 'Stale'}</span> <b>{ro.day.says}</b>
    <span class="muted mono">{wclock(ro.day.since)}–{wclock(ro.day.until)}{#if ro.day.job}{' · '}{ro.day.job.title} at the {ro.day.job.place ?? ro.day.job.at}, {String(ro.day.job.open).padStart(2, '0')}:00–{String(ro.day.job.close).padStart(2, '0')}:00{/if}</span></p>
  {#if dayLog.length}<ol class="daylog mono">{#each dayLog as d}<li><span class="muted">{wclock(d.at)}</span> {d.says}</li>{/each}</ol>{/if}
</section>
{/if}
{#if ro?.online && ro.routine}
<section class="frame">
  <h3 class="caps">Its day <span class="muted">· its ring — what it does, where, at whose things; the dot is the world clock; a fan is a decision</span></h3>
  <RingView routine={ro.routine} decisions={ro.decisions ?? []} live={!!ro.online} />
</section>
{/if}
<section class="frame trace-sec">
  <h3 class="caps">Trace log <span class="muted">· {ro?.online && ro.routine ? 'its routine as a graph — the dot where it is, a knot where it discovered something; 75 % / 25 %: the choices fanning out of them' : 'its loop and the choices it made, newest right'}</span></h3>
  <TraceView decisions={ro?.decisions ?? []} states={graph?.states ?? []} current={here} live={!!ro?.online} routine={ro?.online ? ro.routine ?? null : null} bind:sel />
</section>
<div class="two">
  <section class="frame">
    <h3 class="caps">Its loop</h3>
    {#if graph}<LoopDiagram states={graph.states} transitions={graph.transitions} initial={graph.initial} current={here} inside={ro?.online && ro.behaviour ? `${ro.behaviour}: ${ro.state}` : null} loop={ro?.loop ?? []} />{:else}<p class="muted">{error ?? 'Loading its graph…'}</p>{/if}
  </section>
  <section class="frame">
    <h3 class="caps">Decision</h3>
    <DecisionTree decisions={ro?.decisions ?? []} bind:sel />
  </section>
</div>
<div class="panel">
  <section class="tree frame mono scroll">
    {#if error}<p class="muted">{error}</p>{:else if !graph}<p class="muted">Loading its graph…</p>{:else}
      {#each [...graph.states].sort((a, b) => (a.name === graph!.initial ? -1 : b.name === graph!.initial ? 1 : 0)) as s}
        <div class="state" class:initial={s.name === graph.initial} class:now={here === s.name}>
          <div class="head"><b>{s.name}</b>{#if s.name === graph.initial}<em>initial</em>{/if}{#if here === s.name}<em class="nowtag">● now{ro?.behaviour ? ` · in ${ro.behaviour}: ${ro.state}` : ''}</em>{/if}<span class="muted">L{s.lod ?? 1}</span></div>
          {#each s.do as t}<div class="task">⤷ {task(t)}</div>{/each}
          {#each graph.transitions.filter((t) => t.from === s.name) as t}<div class="tr">→ {t.to} <span class="muted">on {trig(t.trigger)}</span></div>{/each}
        </div>
      {/each}
      {#if graph.transitions.some((t) => t.from === '*')}
        <div class="state"><div class="head"><b>* any state</b></div>{#each graph.transitions.filter((t) => t.from === '*') as t}<div class="tr">→ {t.to} <span class="muted">on {trig(t.trigger)}</span></div>{/each}</div>
      {/if}
    {/if}
  </section>
  <aside>
    <section class="frame readout">
      <h3 class="caps">Readout</h3>
      {#if ro?.online}
        <p class="on caps">● Live — {ro.place}</p>
        <p class="line"><b>{ro.state}</b>{#if ro.behaviour}{' · '}running {ro.behaviour}'s behaviour{/if}{#if ro.held}{' · '}holding <b>{ro.held}</b>{/if}</p>
        <p class="muted mono small">knows {ro.known} · perceives {(ro.layers ?? []).join(' + ')} · L{ro.lod}{#if ro.since} · placed {new Date(ro.since).toLocaleTimeString()}{/if}</p>
        {#if ro.map && ro.pos}
          <svg class="map" viewBox="0 0 100 100" role="img" aria-label={`Map of ${ro.place}`}>
            <rect x="0" y="0" width="100" height="100" class="ground" />
            {#each things as o}<g class="thing"><rect x={mx(o.x) - 1.6} y={mz(o.z) - 1.6} width="3.2" height="3.2" /><text x={mx(o.x) > 66 ? mx(o.x) - 2.6 : mx(o.x) + 2.6} y={mz(o.z) + 1.2 + o.dy} text-anchor={mx(o.x) > 66 ? 'end' : 'start'}>{o.n}</text></g>{/each}
            <g class="me" transform={`translate(${mx(ro.pos[0])} ${mz(ro.pos[1])}) rotate(${-(ro.yaw ?? 0)})`}><circle r="2.6" /><path d="M0 0 L0 5" /></g>
          </svg>
        {/if}
        {#if ro.drives}<div class="drv">{#each Object.entries(ro.drives) as [n, v]}<span class="caps">{n}</span><span class="bar"><i style={`width:${Math.min(100, v)}%`}></i></span><span class="mono">{Math.round(v)}</span>{/each}</div>{/if}
        {#if ro.events?.length}<ol class="ev mono">{#each [...ro.events].reverse().slice(0, 8) as e}<li>{e}</li>{/each}</ol>{/if}
      {:else}
        <p class="off caps">● Offline</p>
        <p class="muted">{rec.tenant ? 'Give it a home in the Control Panel (The Plaza) and its host places it there — it lives its day while no one watches, and what it learns is written into its DB.' : 'A stock class lives nowhere — it is the template NPCs are stamped from.'}</p>
      {/if}
    </section>
    <section class="frame">
      <h3 class="caps">Runs as</h3>
      <pre class="mono">{JSON.stringify(rec.role, null, 1)}</pre>
    </section>
  </aside>
</div>
</div>

<style>
  .wrap { padding: 18px; display: flex; flex-direction: column; gap: 14px; }
  .two { display: grid; grid-template-columns: 1fr 1.15fr; gap: 14px; align-items: start; }
  .trace-sec h3 span, .today h3 span { text-transform: none; letter-spacing: 0; font-weight: 400; }
  .daynow { margin: 0 0 6px; font-size: 16px; display: flex; gap: 12px; align-items: baseline; flex-wrap: wrap; } .daynow b { color: var(--cyan); font-weight: 600; }
  .daynow .on, .daynow .off { font-size: 12px; margin: 0; }
  .daylog { margin: 0; padding-left: 18px; font-size: 12px; color: #9fb0bb; } .daylog li { margin: 2px 0; }
  .panel { display: grid; grid-template-columns: 1.4fr 1fr; gap: 14px; align-items: start; }
  section { background: var(--panel); padding: 12px 14px; }
  .tree { max-height: 62vh; overflow: auto; }
  .state { border-left: 2px solid var(--line-dim); padding: 4px 0 8px 12px; margin-bottom: 8px; }
  .state.initial { border-left-color: var(--cyan); }
  .head { display: flex; gap: 10px; align-items: baseline; }
  .head b { color: var(--text); font-size: 13px; }
  .head em { font-style: normal; color: var(--cyan); font-size: 11px; }
  .task { color: var(--amber); padding-left: 10px; }
  .tr { color: #9fb0bb; padding-left: 10px; }
  aside { display: flex; flex-direction: column; gap: 14px; }
  h3 { margin: 0 0 8px; font-size: 13px; color: var(--muted); font-weight: 600; }
  .off { color: var(--red); margin: 0 0 6px; font-weight: 600; }
  .on { color: var(--green); margin: 0 0 6px; font-weight: 600; }
  .line { margin: 0 0 4px; font-size: 16px; } .line b { color: var(--cyan); font-weight: 600; }
  .small { font-size: 11px; margin: 0 0 8px; }
  .state.now { border-left-color: var(--green); background: rgba(25, 195, 125, .07); }
  .nowtag { color: var(--green) !important; }
  .map { width: 100%; max-width: 360px; aspect-ratio: 1; display: block; margin: 6px auto 10px; border: 1px solid var(--line-dim); }
  .map .ground { fill: #0b1015; }
  .map .thing rect { fill: #24323e; stroke: #3d5568; stroke-width: .3; }
  .map .thing text { fill: #6f8593; font: 2.4px var(--mono); }
  .map .me circle { fill: var(--cyan); stroke: #001018; stroke-width: .6; }
  .map .me path { stroke: var(--cyan); stroke-width: 1; }
  .drv { display: grid; grid-template-columns: 80px 1fr 30px; gap: 4px 8px; align-items: center; font-size: 13px; margin-bottom: 8px; }
  .drv .bar { height: 5px; background: #1b242d; } .drv .bar i { display: block; height: 100%; background: var(--cyan); }
  .ev { margin: 0; padding: 0; list-style: none; font-size: 11px; color: #9fb0bb; max-height: 160px; overflow: auto; }
  .ev li { border-left: 2px solid var(--line-dim); padding-left: 8px; }
  .ev li:first-child { border-left-color: var(--green); color: var(--text); }
  pre { margin: 0; white-space: pre-wrap; color: #b9c7d0; max-height: 300px; overflow: auto; }
  @media (max-width: 800px) { .panel, .two { grid-template-columns: 1fr; } .wrap { padding: 12px; } }
</style>
